import React, { useState, useEffect } from 'react';
import { FileText, Loader, CheckCircle, XCircle, Award, Download, Upload,} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserMarkSchemes, getUserPreferences, getUserData } from '../services/database';
import { markStudentWork } from '../services/openaiAPI';
import { auth, db } from '../services/firebase';
import { doc, setDoc, addDoc, collection } from 'firebase/firestore';
import AppNavBar from './AppNavBar';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../contexts/ModalContext';



const MarkWorkPage = () => {
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [studentFile, setStudentFile] = useState(null);
  const [markingResult, setMarkingResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [userLevel, setUserLevel] = useState('');
  const [generateDownload, setGenerateDownload] = useState(false);
  const { showAlert } = useModal();
  const navigate = useNavigate();
  const [contextOpen, setContextOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // State for mark scheme input method
  const [schemeInputMethod, setSchemeInputMethod] = useState(null);
  const [textMarkScheme, setTextMarkScheme] = useState('');
  
  // NEW: State for additional context
  const [additionalContext, setAdditionalContext] = useState('');
  
  // OCR Progress states
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStatus, setOcrStatus] = useState('');
  
  const { currentUser } = useAuth();

  // Load user's schemes and preferences when component mounts
  useEffect(() => {
    loadUserData();
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const [userSchemes, userPrefs] = await Promise.all([
        getUserMarkSchemes(currentUser.uid),
        getUserPreferences(currentUser.uid)
      ]);
      
      setSchemes(userSchemes);
      setUserLevel(userPrefs?.level || '');
      console.log('User teaching level:', userPrefs?.level);
    } catch (error) {
      console.error('Error loading user data:', error);
      await showAlert('Failed to load data: ' + error.message, 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    
    const validFiles = [];
    
    for (const file of files) {
      const fileName = file.name.toLowerCase();
    
      if (fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
        await showAlert(`PowerPoint files need to be converted to PDF first.\n\nQUICK CONVERSION:\n1. Open PowerPoint → File → Export → Create PDF/XPS Document\n2. Upload the PDF file for accurate AI analysis`, 'Unsupported Format');
        event.target.value = '';
        setStudentFile(null);
        return;
      }
    
      if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
        await showAlert(`Word documents need to be converted to PDF first.\n\nQUICK CONVERSION:\n1. Open Word → File → Save As → PDF\n2. Upload the PDF file instead`, 'Unsupported Format');
        event.target.value = '';
        setStudentFile(null);
        return;
      }
    
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') &&
          !fileName.endsWith('.png') && !fileName.endsWith('.gif') && !fileName.endsWith('.bmp') &&
          !fileName.endsWith('.webp') && !fileName.endsWith('.txt')) {
        await showAlert(`Only PDF, image files, and plain text are supported.\n\nSUPPORTED FORMATS:\n- PDF files (best for documents)\n- Images: JPG, PNG, GIF, BMP, WebP\n- Plain text (.txt)`, 'Unsupported Format');
        event.target.value = '';
        setStudentFile(null);
        return;
      }
    
      validFiles.push(file);
    }
    
    setStudentFile(validFiles[0] || null);
    event.target.value = '';
  };

  const processFile = async (file) => {
    if (!file) return;
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
      await showAlert('PowerPoint files need to be converted to PDF first.', 'Unsupported Format');

      return;
    }
    if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
      await showAlert('Word documents need to be converted to PDF first for accurate grading.', 'Unsupported Format');
      return;
    }
    if (!fileName.endsWith('.pdf') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') &&
        !fileName.endsWith('.png') && !fileName.endsWith('.gif') && !fileName.endsWith('.bmp') &&
        !fileName.endsWith('.webp') && !fileName.endsWith('.txt')) {
          await showAlert('Only PDF, image files, and plain text are supported.', 'Unsupported Format');
      return;
    }
    setStudentFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    await processFile(file);
  };

  const getMarkSchemeData = () => {
    if (schemeInputMethod === 'upload') {
      return schemes.find(s => s.id === selectedScheme);
    } else {
      return {
        id: 'text-input',
        name: 'Custom Mark Scheme',
        fileURL: 'data:text/plain;base64,' + btoa(unescape(encodeURIComponent(textMarkScheme)))
      };
    }
  };

  // Function to validate mark scheme selection
  const isMarkSchemeValid = () => {
    if (schemeInputMethod === 'upload') {
      return selectedScheme !== '';
    } else {
      return textMarkScheme.trim() !== '';
    }
  };

  const handleStartMarking = async () => {
    // Check validation based on mode
    if (!isMarkSchemeValid() || !studentFile) {
      await showAlert('Please select/enter a mark scheme and upload a student file', 'Missing Fields');
      return;
    }

    setMarking(true);
    setOcrProgress(0);
    setOcrStatus('');
    
    const scheme = getMarkSchemeData();
    
    try {
        console.log('🤖 Starting AI marking with OCR support...');
        console.log('User level:', userLevel);
        console.log('Generate download:', generateDownload);
        console.log('Scheme input method:', schemeInputMethod);
        console.log('Scheme data:', scheme);
        console.log('Additional context:', additionalContext);
        
        // Monitor for OCR progress in console
        const originalConsoleLog = console.log;
        console.log = (...args) => {
          const message = args.join(' ');
          if (message.includes('OCR Progress')) {
            const match = message.match(/(\d+)%/);
            if (match) {
              setOcrProgress(parseInt(match[1]));
            }
          }
          if (message.includes('Starting OCR') || message.includes('Converting PDF') || message.includes('OCR completed')) {
            setOcrStatus(message.replace(/🔍|📄|✅/g, '').trim());
          }
          originalConsoleLog(...args);
        };
        
        const result = await markStudentWork(studentFile, scheme, userLevel, false, additionalContext);
        
        // Restore original console.log
        console.log = originalConsoleLog;
        
        console.log('✅ AI marking completed:', result);
        
        // Handle the marking result
        if (userLevel === 'College/University' && result.overallGrade) {
          const cleanResult = {
            overallGrade: String(result.overallGrade),
            feedback: String(result.feedback || ''),
            strengths: Array.isArray(result.strengths) ? result.strengths : ['Analysis provided'],
            improvements: Array.isArray(result.improvements) ? result.improvements : ['See feedback'],
            schemeName: scheme.name,
            studentFileName: studentFile.name,
            criteriaAssessment: result.criteriaAssessment || null,
            taskBreakdown: result.taskBreakdown || null,
            criteriaBreakdown: result.criteriaBreakdown || null,  
            gradingExplanation: result.gradingExplanation || null 
          };
          console.log('🧹 Cleaned result for college user:', cleanResult);
          setMarkingResult(cleanResult);
        } else {
          // Ensure we have a valid result structure
          const processedResult = {
            ...result,
            overallGrade: result.overallGrade || null,
            score: result.score || 75, // Default score if missing
            feedback: result.feedback || 'Assessment completed',
            strengths: Array.isArray(result.strengths) ? result.strengths : ['Work submitted'],
            improvements: Array.isArray(result.improvements) ? result.improvements : ['See feedback'],
            schemeName: scheme.name,
            studentFileName: studentFile.name
          };
          setMarkingResult(processedResult);
        }
        
        // Handle automatic download if requested
        if (generateDownload) {
          handleManualDownload();
        }
        
        if (currentUser) {
          try {
            const currentUserData = await getUserData(currentUser.uid);
            if (currentUserData) {
              const currentUsage = currentUserData.assessUsage || 0;
              const updatedData = {
                ...currentUserData,
                assessUsage: currentUsage + 1,
                lastMarking: new Date().toISOString()
              };
              await setDoc(doc(db, 'users', currentUser.uid), updatedData);
            }
        
            // Save marking insight to Firebase
            const insightData = {
              userId: currentUser.uid,
              briefId: scheme.id,
              briefName: scheme.name,
              overallGrade: result.overallGrade || null,
              score: result.score || null,
              criteriaBreakdown: result.criteriaBreakdown || [],
              timestamp: new Date().toISOString(),
              userLevel
            };

            await addDoc(collection(db, 'markingInsights'), insightData);
            console.log('✅ Marking insight saved to Firebase');
        
          } catch (usageError) {
            console.error('❌ Failed to update usage tracking:', usageError);
          }
        }
        
      } catch (error) {
        console.error('AI Marking failed:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        
        // Show detailed error to user
        await showAlert(`AI Marking failed: ${error.message}\n\nPlease check:\n1. Your internet connection\n2. The file format is supported\n3. Try again in a moment`, 'Error');
        
        // Create a proper fallback result
        const isCollegeLevel = userLevel === 'College/University';
        const mockResult = {
          ...(isCollegeLevel 
            ? { overallGrade: 'Pass' }
            : { score: 75, percentage: 75 }
          ),
          feedback: `AI marking temporarily unavailable due to: ${error.message}.\n\nThis is a fallback result for "${scheme.name}". Please try again later.`,
          strengths: ["File uploaded successfully", "Marking attempted"],
          improvements: ["AI marking failed - please retry", "Check file format and size"],
          schemeName: scheme.name,
          studentFileName: studentFile.name
        };
        
        setMarkingResult(mockResult);
      } finally {
        setMarking(false);
        setOcrProgress(0);
        setOcrStatus('');
      }
  };

  const resetMarking = () => {
    setMarkingResult(null);
    setStudentFile(null);
    setSelectedScheme('');
    setGenerateDownload(false);
    setTextMarkScheme('');
    setAdditionalContext('');
    setSchemeInputMethod(null);
    setOcrProgress(0);
    setOcrStatus('');
  };

  const handleManualDownload = async () => {
    if (!markingResult) return;
    
    try {
      console.log('📥 Starting manual download...');
      console.log('Marking result:', markingResult);
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          markingResult,
          studentFileName: studentFile?.name || 'student-work',
          userLevel
        })
      });
      
      console.log('📡 Download API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📄 Download data received:', data);
        
        if (data.downloadUrl) {
          // Instead of downloading, open in a new tab for easy copying
          const newWindow = window.open('', '_blank');
          newWindow.document.write(`
            <html>
              <head>
                <title>TeAIch Feedback Report</title>
                <style>
                  body { 
                    font-family: Arial, sans-serif; 
                    max-width: 800px; 
                    margin: 20px auto; 
                    padding: 20px;
                    line-height: 1.6;
                  }
                  .copy-instructions {
                    background: #e3f2fd;
                    padding: 15px;
                    border-radius: 5px;
                    border-left: 4px solid #2196f3;
                    margin-bottom: 20px;
                  }
                  .content {
                    background: #f9f9f9;
                    padding: 20px;
                    border-radius: 5px;
                    white-space: pre-wrap;
                    font-family: 'Times New Roman', serif;
                  }
                  .copy-button {
                    background: #2874A6;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 5px;
                    cursor: pointer;
                    font-size: 16px;
                    margin-bottom: 20px;
                  }
                  .copy-button:hover {
                    background: #1e5f8b;
                  }
                </style>
              </head>
              <body>
                <div class="copy-instructions">
                  <h3>📋 How to use this report:</h3>
                  <ol>
                    <li>Click "Copy Report" below</li>
                    <li>Open Microsoft Word</li>
                    <li>Paste (Ctrl+V or Cmd+V)</li>
                    <li>Apply formatting as needed</li>
                  </ol>
                </div>
                
                <button class="copy-button" onclick="copyContent()">📋 Copy Report</button>
                
                <div class="content" id="reportContent">${atob(data.downloadUrl.split(',')[1])}</div>
                
                <script>
                  function copyContent() {
                    const content = document.getElementById('reportContent').textContent;
                    navigator.clipboard.writeText(content).then(() => {
                      alert('Report copied to clipboard! Now paste it into Microsoft Word.');
                    }).catch(err => {
                      console.error('Failed to copy: ', err);
                      // Fallback: select the text
                      const range = document.createRange();
                      range.selectNode(document.getElementById('reportContent'));
                      window.getSelection().removeAllRanges();
                      window.getSelection().addRange(range);
                      alert('Text selected. Press Ctrl+C (or Cmd+C) to copy, then paste into Word.');
                    });
                  }
                </script>
              </body>
            </html>
          `);
          
          console.log('✅ Report opened in new tab for copying');
        } else {
          throw new Error('No download URL received');
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Download API error:', errorData);
        throw new Error(`Download failed: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Manual download failed:', error);
      await showAlert(`Download failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`, 'Error');
    }
  };

  const downloadSingleFileReport = () => {
    if (!markingResult) return;

    const doc = new jsPDF();
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const addText = (text, fontSize = 11, bold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ''), contentWidth);
      lines.forEach(line => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(line, margin, y);
        y += fontSize * 0.45;
      });
      y += 3;
    };

    const addDivider = () => {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
    };

    // Header
    addText('TeAIch Marking Report', 18, true);
    addText(`Uploaded document: ${markingResult.studentFileName || 'Unknown'}`, 11);
    addText(`Mark Scheme: ${markingResult.schemeName || 'Unknown'}`, 11);
    addText(`Date: ${new Date().toLocaleDateString('en-GB')}`, 11);
    y += 4;
    addDivider();

    // Grade
    if (userLevel === 'College/University' && markingResult.overallGrade) {
      addText('Overall Grade', 13, true);
      addText(markingResult.overallGrade, 16, true);
      y += 4;
      addDivider();
    } else if (markingResult.score !== undefined) {
      addText('Score', 13, true);
      addText(`${markingResult.score} / 100`, 16, true);
      y += 4;
      addDivider();
    }

    // Criteria breakdown
    if (userLevel === 'College/University' && markingResult.criteriaBreakdown?.length > 0) {
      addText('Criteria Assessment', 13, true);
      y += 2;
      const sorted = [...markingResult.criteriaBreakdown].sort((a, b) => {
        const getCode = str => str.match(/^[A-Z]\d+/)?.[0] || '';
        const order = code => {
          const letter = code[0];
          const num = parseInt(code.slice(1));
          const letterOrder = { P: 0, M: 1, D: 2 };
          return (letterOrder[letter] ?? 99) * 100 + num;
        };
        return order(getCode(a)) - order(getCode(b));
      });
      sorted.forEach(criterion => {
        const isAchieved = criterion.toLowerCase().includes('achieved') && !criterion.toLowerCase().includes('not achieved');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...(isAchieved ? [22, 163, 74] : [185, 28, 28]));
        const lines = doc.splitTextToSize(criterion, contentWidth);
        lines.forEach(line => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(line, margin, y);
          y += 5;
        });
        y += 2;
      });
      y += 2;
      addDivider();
    }

    // Grading explanation
    if (userLevel === 'College/University' && markingResult.gradingExplanation) {
      addText('Grading Decision', 13, true);
      doc.setTextColor(0, 0, 0);
      addText(markingResult.gradingExplanation, 10);
      y += 2;
      addDivider();
    }

    // Feedback
    addText('AI Feedback', 13, true);
    doc.setTextColor(0, 0, 0);
    addText(markingResult.feedback, 10);
    y += 2;
    addDivider();

    // Strengths
    if (markingResult.strengths?.length > 0) {
      addText('Strengths', 13, true);
      markingResult.strengths.forEach(s => {
        doc.setTextColor(0, 0, 0);
        addText(`• ${s}`, 10);
      });
      y += 2;
    }

    // Improvements
    if (markingResult.improvements?.length > 0) {
      addText('Areas for Improvement', 13, true);
      markingResult.improvements.forEach(s => {
        doc.setTextColor(0, 0, 0);
        addText(`• ${s}`, 10);
      });
      y += 2;
    }

    addDivider();
    doc.setTextColor(150, 150, 150);
    addText('Generated by TeAIch Suite', 9);

    const filename = `TeAIch-Report-${(markingResult.studentFileName || 'report').replace('.pdf', '').replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
    doc.save(filename);
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-ink-600';
    const lowerGrade = grade.toLowerCase();
    if (lowerGrade.includes('distinction')) return 'text-purple-600';
    if (lowerGrade.includes('merit')) return 'text-blue-600';
    if (lowerGrade.includes('pass')) return 'text-green-600';
    return 'text-coral-600';
  };

  const getGradeBadge = (grade) => {
    if (!grade) return 'bg-sand-100 text-ink-800';
    const lowerGrade = grade.toString().toLowerCase();
    if (lowerGrade.includes('distinction')) return 'bg-purple-100 text-purple-800';
    if (lowerGrade.includes('merit')) return 'bg-blue-100 text-blue-800';
    if (lowerGrade.includes('pass')) return 'bg-green-100 text-green-800';
    if (lowerGrade.includes('fail')) return 'bg-red-100 text-red-800';
    return 'bg-coral-100 text-coral-800';
  };

  const renderScore = () => {
    const isCollegeLevel = userLevel === 'College/University';
    
    if (!markingResult) return null;
    
    if (isCollegeLevel && markingResult.overallGrade) {
      const gradeBadgeClass = getGradeBadge(markingResult.overallGrade);
      return (
        <div className="text-center">
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${gradeBadgeClass || 'bg-sand-100 text-ink-800'}`}>
            <Award className="w-5 h-5 mr-2" />
            {String(markingResult.overallGrade).toUpperCase()}
          </div>
        </div>
      );
    }
    else if (!isCollegeLevel && markingResult.score) {
      return (
        <div className="text-center">
          <span className="text-3xl font-bold text-coral-600">{markingResult.score}</span>
          <span className="text-xl text-ink-600">
            {markingResult.percentage ? ` (${markingResult.percentage}%)` : '/100'}
          </span>
        </div>
      );
    }
    else if (markingResult.overallGrade) {
      const gradeBadgeClass = getGradeBadge(markingResult.overallGrade);
      return (
        <div className="text-center">
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${gradeBadgeClass || 'bg-sand-100 text-ink-800'}`}>
            <Award className="w-5 h-5 mr-2" />
            {String(markingResult.overallGrade).toUpperCase()}
          </div>
        </div>
      );
    } else if (markingResult.score) {
      return (
        <div className="text-center">
          <span className="text-3xl font-bold text-coral-600">{markingResult.score}</span>
          <span className="text-xl text-ink-600">
            {markingResult.percentage ? ` (${markingResult.percentage}%)` : '/100'}
          </span>
        </div>
      );
    }
    return <div className="text-center text-ink-500">No score available</div>;
  };

  const renderCriteriaBreakdown = () => {
    if (!markingResult?.criteriaBreakdown || markingResult.criteriaBreakdown.length === 0) return null;

    return (
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border-4 border-ink-900">
        <h4 className="font-bold mb-3 text-ink-800">📋 Criteria Assessment:</h4>
        <div className="space-y-2">
        {[...markingResult.criteriaBreakdown].sort((a, b) => {
              const getCode = str => str.match(/^[A-Z]\d+/)?.[0] || '';
              const order = code => {
                const letter = code[0];
                const num = parseInt(code.slice(1));
                const letterOrder = { P: 0, M: 1, D: 2 };
                return (letterOrder[letter] ?? 99) * 100 + num;
              };
              return order(getCode(a)) - order(getCode(b));
            }).map((criterion, index) => {
            const isAchieved = criterion.toLowerCase().includes('achieved') && 
                              !criterion.toLowerCase().includes('not achieved');
            return (
              <div key={index} className="flex items-start space-x-2 p-2 bg-white rounded">
                {isAchieved ? (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <span className={`text-sm ${isAchieved ? 'text-green-700' : 'text-red-700'}`}>
                  {criterion}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderGradingExplanation = () => {
    if (!markingResult?.gradingExplanation) return null;
    
    return (
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-4 border-ink-900">
        <h4 className="font-bold mb-2 text-ink-800">🎯 Grading Decision:</h4>
        <p className="text-ink-700">{markingResult.gradingExplanation}</p>
      </div>
    );
  };

  const renderTaskBreakdown = () => {
    if (!markingResult?.taskBreakdown) return null;

    return (
      <div className="mt-6 p-4 bg-sand-50 rounded-lg border-4 border-ink-900">
        <h4 className="font-bold mb-3 text-ink-800">Task Assessment Breakdown:</h4>
        <div className="space-y-3">
          {Object.entries(markingResult.taskBreakdown).map(([task, result]) => {
            const isMet = result.toLowerCase().includes('met') && !result.toLowerCase().includes('not met');
            return (
              <div key={task} className="flex items-start space-x-3 p-3 bg-white rounded border">
                <div className="flex items-center space-x-2 min-w-0">
                  {isMet ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                  <span className="font-medium text-ink-800 min-w-0">{task}:</span>
                </div>
                <span className={`text-sm flex-1 ${isMet ? 'text-green-700' : 'text-red-700'}`}>
                  {result}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCriteriaAssessment = () => {
    if (!markingResult?.criteriaAssessment && !markingResult?.markBreakdown) return null;

    return (
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border-4 border-ink-900">
        <h4 className="font-bold mb-2 text-blue-800">
          {markingResult.criteriaAssessment ? 'Criteria Assessment:' : 'Mark Breakdown:'}
        </h4>
        <p className="text-ink-700">
          {markingResult.criteriaAssessment || markingResult.markBreakdown}
        </p>
      </div>
    );
  };

  const getScoreLabel = () => {
    const isCollegeLevel = userLevel === 'College/University';
    if (isCollegeLevel && markingResult?.overallGrade) return 'Grade:';
    if (!isCollegeLevel && markingResult?.score) return 'Score:';
    return markingResult?.overallGrade ? 'Grade:' : 'Score:';
  };

  
  const renderUploadSection = () => (
    <div className="space-y-6">

      {/* Section 1 — Student work */}
      <div>
        <p className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-3">Student work</p>

        {studentFile ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-sm font-medium text-green-800 flex-1">{studentFile.name}</span>
            <button
              onClick={() => setStudentFile(null)}
              className="text-xs bg-sand-100 text-ink-700 px-3 py-1 rounded hover:bg-sand-200 transition-colors"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-coral-500 bg-coral-50' : 'border-sand-300 hover:border-coral-400 hover:bg-sand-50'}`}
            >
              <Upload className="w-7 h-7 mx-auto mb-2 text-coral-400" />
              <p className="text-sm font-medium text-ink-700">Drag & drop</p>
              <p className="text-xs text-ink-500 mt-1">Drop your file here</p>
            </div>

            <label className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-sand-300 hover:border-coral-400 hover:bg-sand-50">
              <FileText className="w-7 h-7 mx-auto mb-2 text-coral-400" />
              <p className="text-sm font-medium text-ink-700">Choose a file</p>
              <p className="text-xs text-ink-500 mt-2 text-center">
                Note: PDFs containing code may not extract perfectly — for best results, paste code into a text file first.
              </p>
              <input
                type="file"
                onChange={handleStudentFileUpload}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.txt"
                className="hidden"
              />
            </label>
          </div>
        )}

        <p className="text-xs text-ink-500 mt-2 text-center">
          Need to upload a new mark scheme?{' '}
          <button onClick={() => navigate('/my-sol')} className="text-coral-600 underline hover:text-coral-800">
            Open the mark scheme library
          </button>
        </p>
      </div>

      {/* Section 2 — Mark scheme */}
      <div>
        <p className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-3">Mark scheme</p>

        {schemeInputMethod === null && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSchemeInputMethod('upload')}
              className="border border-sand-300 rounded-lg p-5 text-center hover:border-coral-400 hover:bg-sand-50 transition-colors"
            >
              <FileText className="w-6 h-6 mx-auto mb-2 text-coral-400" />
              <p className="text-sm font-medium text-ink-700">Choose a mark scheme</p>
              <p className="text-xs text-ink-500 mt-1">From your uploaded library</p>
            </button>
            <button
              onClick={() => setSchemeInputMethod('text')}
              className="border border-sand-300 rounded-lg p-5 text-center hover:border-coral-400 hover:bg-sand-50 transition-colors"
            >
              <Upload className="w-6 h-6 mx-auto mb-2 text-coral-400" />
              <p className="text-sm font-medium text-ink-700">Enter a mark scheme</p>
              <p className="text-xs text-ink-500 mt-1">Type or paste criteria directly</p>
            </button>
          </div>
        )}

        {schemeInputMethod === 'upload' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-ink-700">Choose from your library</p>
              <button onClick={() => { setSchemeInputMethod(null); setSelectedScheme(''); }} className="text-xs bg-sand-100 text-ink-700 px-3 py-1 rounded hover:bg-sand-200 transition-colors">Back</button>
            </div>
            {loading ? (
              <div className="flex items-center p-3 border border-sand-300 rounded-lg">
                <Loader className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm">Loading your schemes...</span>
              </div>
            ) : schemes.length === 0 ? (
              <div className="p-4 border border-sand-300 rounded-lg bg-sand-50 text-center">
                <p className="text-sm text-ink-600 mb-3">No mark schemes uploaded yet.</p>
                <button onClick={() => navigate('/my-sol')} className="bg-coral-300 text-white px-4 py-2 rounded hover:bg-coral-500 transition duration-200 text-sm">
                  Upload a Mark Scheme
                </button>
              </div>
            ) : (
              <select
                value={selectedScheme}
                onChange={(e) => setSelectedScheme(e.target.value)}
                className="w-full p-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                <option value="">-- Choose a scheme --</option>
                {schemes.map((scheme) => (
                  <option key={scheme.id} value={scheme.id}>{scheme.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {schemeInputMethod === 'text' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-ink-700">Enter your criteria</p>
              <button onClick={() => { setSchemeInputMethod(null); setTextMarkScheme(''); }} className="text-xs bg-sand-100 text-ink-700 px-3 py-1 rounded hover:bg-sand-200 transition-colors">Back</button>
            </div>
            <textarea
              value={textMarkScheme}
              onChange={(e) => setTextMarkScheme(e.target.value)}
              placeholder="Enter your marking criteria here. Include grade boundaries, assessment criteria, or rubric details..."
              rows={7}
              className="w-full p-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
          </div>
        )}
      </div>

      {/* Section 3 — Additional context */}
      <div>
        <button
          onClick={() => setContextOpen(!contextOpen)}
          className="w-full flex items-center justify-between p-4 border border-sand-300 rounded-lg hover:border-coral-400 hover:bg-sand-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-coral-50 flex items-center justify-center flex-shrink-0">
              <span className="text-coral-500 text-sm font-medium">?</span>
            </div>
            <div>
              <p className="text-sm font-medium text-ink-700">Want to give our AI more context?</p>
              <p className="text-xs text-ink-500">Add excerpts, instructions, or focus areas</p>
            </div>
          </div>
          <span className={`text-ink-400 text-sm transition-transform duration-200 inline-block ${contextOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {contextOpen && (
          <div className="mt-0 p-4 border border-sand-300 border-t-0 rounded-b-lg">
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="For example: specific text excerpts from Macbeth, assignment context, particular focus areas, or any special considerations..."
              rows={4}
              className="w-full p-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 resize-none"
            />
          </div>
        )}
      </div>

      {/* Start button */}
      <button
        onClick={handleStartMarking}
        disabled={!isMarkSchemeValid() || !studentFile || marking}
        className="w-full bg-coral-300 text-white py-4 rounded-lg hover:bg-coral-500 disabled:bg-ink-400 disabled:cursor-not-allowed transition duration-200 flex items-center justify-center gap-2 font-medium text-base"
      >
        {marking ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            AI is analyzing the work...
          </>
        ) : (
          <>
            <Upload className="w-5 h-5" />
            Start AI marking
          </>
        )}
      </button>

      {marking && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-ink-600 mb-2">
            Our AI is analyzing the work against your mark scheme{additionalContext ? ' and additional context' : ''}. This may take up to 10 seconds.
          </p>
          {ocrProgress > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-ink-500 mb-1">
                <span>OCR Text Extraction</span>
                <span>{ocrProgress}%</span>
              </div>
              <div className="w-full bg-sand-200 rounded-full h-2">
                <div className="bg-coral-600 h-2 rounded-full transition-all duration-300" style={{ width: `${ocrProgress}%` }} />
              </div>
              {ocrStatus && <p className="text-xs text-ink-500 mt-1">{ocrStatus}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );

return (
  <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
    <AppNavBar currentPage="mark-work" />
    
    <div className="max-w-4xl mx-auto px-4 py-8">
    <div className="text-center mb-8">
      <h1 className="text-5xl md:text-6xl font-bold text-coral-500 leading-tight mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
        Mark Student Work
      </h1>
    </div>
      
      <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-8">
        {markingResult ? (
          <div className="space-y-6">
            {userLevel === 'College/University' ? (
              <div className="bg-white p-6 rounded-lg shadow-md border-4 border-ink-900">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-ink-800 mb-4">AI Marking Complete</h3>
                  <div className="inline-flex items-center px-6 py-3 bg-blue-100 text-blue-800 rounded-full text-xl font-bold">
                    <Award className="w-6 h-6 mr-2" />
                    {markingResult?.overallGrade ? String(markingResult.overallGrade).toUpperCase() : 'MERIT'}
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border-4 border-ink-900">
                  <h4 className="font-bold mb-2 text-blue-800">AI Feedback:</h4>
                  <p className="text-ink-700 whitespace-pre-line">{markingResult.feedback}</p>
                </div>

                {renderGradingExplanation()}
                {renderCriteriaBreakdown()}
                
                <div className="grid md:grid-cols-2 gap-6 mt-12">
                  <div className="bg-green-50 p-4 rounded-lg border-4 border-ink-900">
                    <h4 className="font-bold mb-3 text-green-700">Strengths:</h4>
                    {markingResult?.strengths?.map((strength, index) => (
                      <p key={index} className="text-ink-700 mb-2">• {strength}</p>
                    ))}
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border-4 border-ink-900">
                    <h4 className="font-bold mb-3 text-orange-700">Areas for Improvement:</h4>
                    {markingResult?.improvements?.map((improvement, index) => (
                      <p key={index} className="text-ink-700 mb-2">• {improvement}</p>
                    ))}
                  </div>
                </div>
                
                <div className="flex flex-col items-center gap-3 mt-6">
                  <p className="text-xs text-ink-500 text-center max-w-sm">
                    AI marking is not perfect — please apply your professional judgement before sharing this feedback with a student.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={downloadSingleFileReport}
                      className="bg-coral-300 text-white px-6 py-2 rounded-lg hover:bg-coral-500 transition duration-200 inline-flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </button>
                    <button
                      onClick={resetMarking}
                      className="bg-coral-300 text-white px-6 py-2 rounded-lg hover:bg-coral-500 transition duration-200"
                    >
                      Mark Another Piece of Work
                    </button>
                  </div>
                </div>
                </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-ink-800">AI Marking Complete</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={downloadSingleFileReport}
                      className="bg-coral-300 text-white px-4 py-2 rounded hover:bg-coral-500 transition duration-200 inline-flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </button>
                    <button
                      onClick={resetMarking}
                      className="bg-coral-300 text-white px-4 py-2 rounded hover:bg-coral-500 transition duration-200"
                    >
                      Mark Another
                    </button>
                  </div>
                </div>
                <p className="text-xs text-ink-500">
                  AI marking is not perfect — please apply your professional judgement before sharing this feedback with a student.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 p-4 bg-sand-50 rounded-lg border-4 border-ink-900">
                  <div>
                    <span className="text-sm text-ink-600">Student File:</span>
                    <p className="font-medium">{markingResult?.studentFileName || 'Unknown'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-ink-600">Mark Scheme:</span>
                    <p className="font-medium">{markingResult?.schemeName || 'Unknown'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-ink-600">{getScoreLabel()}</span>
                    {renderScore()}
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border-4 border-ink-900">
                  <h4 className="font-bold mb-2 text-blue-800">AI Feedback:</h4>
                  <p className="text-ink-700 whitespace-pre-line">{markingResult.feedback}</p>
                </div>

                {renderGradingExplanation()}
                {renderCriteriaBreakdown()}
                {renderTaskBreakdown()}
                {renderCriteriaAssessment()}
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg border-4 border-ink-900">
                    <h4 className="font-bold mb-3 text-green-700">Strengths Identified:</h4>
                    <ul className="space-y-2">
                      {markingResult.strengths?.map((strength, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span className="text-ink-700">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border-4 border-ink-900">
                    <h4 className="font-bold mb-3 text-orange-700">Areas for Improvement:</h4>
                    <ul className="space-y-2">
                      {markingResult.improvements?.map((improvement, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-orange-600 mr-2">→</span>
                          <span className="text-ink-700">{improvement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          renderUploadSection()
        )}
      </div>
    </div>
  </div>
);
};

export default MarkWorkPage;